import { cn } from "@/lib/utils";
import { Sparkles, Mic, ListChecks, Trophy, BookOpen, ChevronRight, MessageCircle, Star } from "lucide-react";
import type { Feature } from "@/lib/marketing-constants";

interface FeatureCellProps {
  feature: Feature;
  className?: string;
}

const TONE_CLASSES: Record<Feature["tone"], string> = {
  primary:
    "border-primary/15 bg-gradient-to-br from-primary/[0.04] via-background to-background dark:from-primary/[0.08]",
  amber:
    "border-secondary/20 bg-gradient-to-br from-secondary/[0.05] via-background to-background dark:from-secondary/[0.08]",
  neutral:
    "border-border bg-card",
  success:
    "border-success/20 bg-gradient-to-br from-success/[0.05] via-background to-background dark:from-success/[0.08]",
};

const ICON_BG: Record<Feature["tone"], string> = {
  primary: "bg-primary/10 text-primary",
  amber: "bg-secondary/15 text-secondary",
  neutral: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  success: "bg-success/10 text-success",
};

const SPAN_CLASSES: Record<Feature["span"], string> = {
  default: "col-span-1",
  wide: "col-span-1 md:col-span-2",
  tall: "col-span-1 md:row-span-2",
  large: "col-span-1 md:col-span-2 md:row-span-2",
};

export function FeatureCell({ feature, className }: FeatureCellProps) {
  const Icon = feature.icon;
  return (
    <article
      className={cn(
        "group relative flex h-full flex-col overflow-hidden rounded-2xl border p-6 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/5 sm:p-7",
        TONE_CLASSES[feature.tone],
        SPAN_CLASSES[feature.span],
        className,
      )}
    >
      <div
        className={cn(
          "mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl",
          ICON_BG[feature.tone],
        )}
      >
        <Icon className="h-5 w-5" aria-hidden="true" />
      </div>

      <h3 className="text-lg font-semibold text-foreground">{feature.title}</h3>
      <p className="mt-2 max-w-prose text-sm text-muted-foreground leading-relaxed">
        {feature.description}
      </p>

      <div className="mt-6 grow">
        <Preview preview={feature.preview} />
      </div>
    </article>
  );
}

function Preview({ preview }: { preview: Feature["preview"] }) {
  switch (preview) {
    case "conversation":
      return <ConversationPreview />;
    case "vocabulary":
      return <VocabularyPreview />;
    case "streak":
      return <StreakPreview />;
    case "questions":
      return <QuestionsPreview />;
  }
}

function ConversationPreview() {
  return (
    <div className="rounded-xl border border-border bg-background p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Sparkles className="h-3 w-3" aria-hidden="true" />
        </span>
        <span>Live conversation</span>
      </div>
      <div className="space-y-2.5 text-sm">
        <div className="rounded-lg bg-slate-100 px-3 py-2 text-slate-800 dark:bg-slate-800 dark:text-slate-200">
          What small change could your city make this year?
        </div>
        <div className="rounded-lg bg-primary px-3 py-2 text-primary-foreground">
          Wider bus and bike lanes on the main road.
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-success/30 bg-success/5 px-3 py-2 text-success-foreground">
          <Mic className="h-3.5 w-3.5 text-success" aria-hidden="true" />
          <div className="flex grow items-center gap-0.5">
            {[0.35, 0.7, 0.5, 0.85, 0.45, 0.65].map((h, i) => (
              <span
                key={i}
                aria-hidden="true"
                className="block w-0.5 rounded-full bg-success/70"
                style={{ height: `${h * 14}px` }}
              />
            ))}
          </div>
          <span className="text-xs tabular-nums text-success">00:18</span>
        </div>
      </div>
    </div>
  );
}

function VocabularyPreview() {
  const words = [
    { word: "habit", tier: "Learning", tone: "amber" },
    { word: "commute", tier: "New", tone: "slate" },
    { word: "budget", tier: "Reviewing", tone: "blue" },
    { word: "negotiate", tier: "Mastered", tone: "green" },
  ] as const;
  return (
    <div className="rounded-xl border border-border bg-background p-3 shadow-sm">
      <div className="mb-2 flex items-center justify-between px-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
          <ListChecks className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
          My deck
        </span>
        <span className="tabular-nums">12 words</span>
      </div>
      <ul className="divide-y divide-border/60">
        {words.map((w) => (
          <li
            key={w.word}
            className="flex items-center justify-between px-1 py-2 text-sm"
          >
            <span className="font-medium text-foreground">{w.word}</span>
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
                w.tone === "amber" && "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
                w.tone === "slate" && "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
                w.tone === "blue" && "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200",
                w.tone === "green" && "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
              )}
            >
              {w.tier}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StreakPreview() {
  const days = [0, 1, 1, 0, 1, 1, 1];
  return (
    <div className="rounded-xl border border-border bg-background p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-amber-100 text-amber-600 dark:bg-amber-900/40">
            <Trophy className="h-4 w-4" aria-hidden="true" />
          </span>
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">This week</p>
            <p className="text-sm font-semibold text-foreground">5 of 7 days</p>
          </div>
        </div>
        <span className="rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-semibold text-amber-600 dark:text-amber-400">
          Level 4
        </span>
      </div>
      <div className="flex items-end justify-between gap-1.5">
        {days.map((active, i) => (
          <div
            key={i}
            aria-hidden="true"
            className={cn(
              "h-12 w-full rounded-md transition-colors",
              active
                ? "bg-gradient-to-t from-primary to-blue-400"
                : "bg-slate-200 dark:bg-slate-800",
            )}
          />
        ))}
      </div>
      <div className="mt-2 flex justify-between text-[10px] uppercase tracking-wider text-muted-foreground">
        {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
          <span key={i}>{d}</span>
        ))}
      </div>
    </div>
  );
}

function QuestionsPreview() {
  const items = [
    "What is one habit you have changed in the past year?",
    "How do you decide what is worth your time?",
    "Describe a city that handles traffic well.",
  ];
  return (
    <div className="rounded-xl border border-border bg-background p-3 shadow-sm">
      <div className="mb-2 flex items-center justify-between px-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
          <BookOpen className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
          Today's questions
        </span>
        <span className="tabular-nums">3 of 6</span>
      </div>
      <ul className="space-y-1.5">
        {items.map((q, i) => (
          <li
            key={q}
            className={cn(
              "flex items-center gap-3 rounded-lg border px-3 py-2 text-sm",
              i === 0
                ? "border-primary/30 bg-primary/5 text-foreground"
                : "border-transparent text-muted-foreground",
            )}
          >
            <span
              className={cn(
                "inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold",
                i === 0
                  ? "bg-primary text-primary-foreground"
                  : "border border-border bg-background text-muted-foreground",
              )}
            >
              {i === 0 ? (
                <MessageCircle className="h-3 w-3" aria-hidden="true" />
              ) : (
                i + 1
              )}
            </span>
            <span className="grow truncate">{q}</span>
            {i === 0 && <ChevronRight className="h-3.5 w-3.5 text-primary" aria-hidden="true" />}
          </li>
        ))}
      </ul>
      <div className="mt-2 flex items-center justify-between border-t border-border px-1 pt-2 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <Star className="h-3 w-3 text-amber-500" aria-hidden="true" />
          Adapted to B1
        </span>
        <span>Updated 2m ago</span>
      </div>
    </div>
  );
}
